import type { Tab } from './index';
import { esc, timeAgo } from '../utils';
import { fetchAlpacaNews, flagArticle, type AlpacaArticle } from '../alpaca';
import { getWorkerBase } from '../worker';

interface Article {
  ticker:      string;
  source:      string;
  title:       string;
  link:        string;
  date:        string;
  description: string;
  flag:        string;
}

const NEWS_TICKERS = [
  { ticker:'HAWK',  name:'HawkEye 360' },
  { ticker:'PL',    name:'Planet Labs' },
  { ticker:'SATL',  name:'Satellogic' },
  { ticker:'IONQ',  name:'IonQ' },
  { ticker:'KRMN',  name:'Karman Holdings' },
  { ticker:'VOYG',  name:'Voyager Technologies' },
  { ticker:'FLY',   name:'Firefly Aerospace' },
  { ticker:'MDA',   name:'MDA Space' },
  { ticker:'XNDU',  name:'Xanadu Quantum' },
  { ticker:'INFQ',  name:'Infleqtion' },
  { ticker:'RXRX',  name:'Recursion Pharmaceuticals' },
  { ticker:'TEM',   name:'Tempus AI' },
  { ticker:'SDGR',  name:'Schrodinger' },
  { ticker:'CRSP',  name:'CRISPR Therapeutics' },
  { ticker:'NTLA',  name:'Intellia Therapeutics' },
  { ticker:'BEAM',  name:'Beam Therapeutics' },
  { ticker:'ILMN',  name:'Illumina' },
  { ticker:'GENB',  name:'Generate Biomedicines' },
  { ticker:'KLRA',  name:'Kailera Therapeutics' },
  { ticker:'CBRS',  name:'Cerebras Systems' },
];

const RSS_CACHE = new Map<string, { data: Article[]; ts: number }>();
const RSS_TTL   = 10 * 60 * 1000;

function sourceColor(source: string): string {
  if (source === 'Yahoo Finance') return 'var(--accent)';
  if (source === 'SEC EDGAR')     return 'var(--warn)';
  if (source === 'Google News')   return 'var(--accent2)';
  if (source === 'PRNewswire')    return '#c084fc';
  return 'var(--text3)';
}

export class NewsFeedTab implements Tab {
  readonly id = 'news';

  private articles: Article[] = [];
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  private el(id: string): HTMLElement { return document.getElementById(id) as HTMLElement; }

  init(): void {
    this.el('news-refresh-btn').addEventListener('click', () => this.fetchAll());
    this.el('news-search').addEventListener('input',             () => this.filter());
    this.el('news-ticker-filter').addEventListener('change',     () => this.filter());
    this.el('news-source-filter').addEventListener('change',     () => this.filter());
    this.el('news-flag-filter').addEventListener('change',       () => this.filter());
  }

  render(): void {
    this.fetchAll();
    if (!this.refreshTimer) {
      this.refreshTimer = setInterval(() => this.fetchAll(), 15 * 60 * 1000);
    }
  }

  destroy(): void {
    if (this.refreshTimer) { clearInterval(this.refreshTimer); this.refreshTimer = null; }
  }

  // ── Fetch ─────────────────────────────────────────────────────────────────

  private async fetchAll(): Promise<void> {
    this.el('news-loading').style.display = 'block';
    this.el('news-feed').innerHTML = '';
    this.el('news-empty').style.display  = 'none';
    this.el('news-error').style.display  = 'none';

    const statusEl = this.el('news-last-updated');
    statusEl.textContent = 'Fetching news…';

    const alpKey    = localStorage.getItem('meridian_alpaca_key')    ?? '';
    const alpSecret = localStorage.getItem('meridian_alpaca_secret') ?? '';
    let all: Article[] = [];

    if (alpKey && alpSecret) {
      statusEl.innerHTML = '<span style="color:var(--green);">⚡ Fetching from Alpaca · Benzinga…</span>';
      const tickers = NEWS_TICKERS.map(t => t.ticker);
      for (let i = 0; i < tickers.length; i += 10) {
        const news = await fetchAlpacaNews(tickers.slice(i, i + 10), 20);
        if (news) all = all.concat(news.map(a => this.fromAlpaca(a)));
        if (i + 10 < tickers.length) await new Promise(r => setTimeout(r, 200));
      }
    }

    // Macro + SEC + optional RSS fallback
    const macroTasks: { url: string; source: string; ticker: string }[] = [
      { url:`https://news.google.com/rss/search?q=${encodeURIComponent('Iran war military strike ceasefire 2026')}&hl=en-US&gl=US&ceid=US:en`, source:'Google News', ticker:'🌍 IRAN' },
      { url:`https://news.google.com/rss/search?q=${encodeURIComponent('CLARITY Act crypto regulation Washington 2026')}&hl=en-US&gl=US&ceid=US:en`, source:'Google News', ticker:'⚖️ CLARITY' },
    ];
    for (const t of ['HAWK','PL','IONQ','CRSP','GENB','CBRS','KRMN']) {
      const start = new Date(Date.now()-7*86400000).toISOString().split('T')[0];
      const end   = new Date().toISOString().split('T')[0];
      macroTasks.push({ url:`https://efts.sec.gov/LATEST/search-index?q=%22${t}%22&dateRange=custom&startdt=${start}&enddt=${end}&forms=8-K,S-1,13F`, source:'SEC EDGAR', ticker:t });
    }
    if (!alpKey || !alpSecret) {
      statusEl.innerHTML = '<span style="color:var(--warn);">🟡 No Alpaca keys — using RSS fallback…</span>';
      for (const { ticker, name } of NEWS_TICKERS) {
        macroTasks.push({ url:`https://feeds.finance.yahoo.com/rss/2.0/headline?s=${ticker}&region=US&lang=en-US`, source:'Yahoo Finance', ticker });
        macroTasks.push({ url:`https://news.google.com/rss/search?q=${encodeURIComponent(name+' stock')}&hl=en-US&gl=US&ceid=US:en`, source:'Google News', ticker });
      }
    }

    for (let i = 0; i < macroTasks.length; i += 5) {
      const batch = macroTasks.slice(i, i + 5);
      const results = await Promise.allSettled(batch.map(t => this.fetchRSS(t.url, t.source, t.ticker)));
      results.forEach(r => { if (r.status === 'fulfilled') all = all.concat(r.value); });
      if (i + 5 < macroTasks.length) await new Promise(r => setTimeout(r, 300));
    }

    // Deduplicate + sort
    const seen = new Set<string>();
    this.articles = all
      .filter(a => { const k = a.title.toLowerCase().trim(); if (seen.has(k)) return false; seen.add(k); return true; })
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

    this.el('news-loading').style.display  = 'none';
    this.el('news-article-count').textContent = String(this.articles.length);
    this.el('news-source-badge').textContent  = alpKey ? '⚡ Alpaca · Benzinga' : '🟡 RSS Fallback';

    if (!this.articles.length) {
      this.el('news-error').style.display = 'block';
      this.el('news-error').textContent = alpKey
        ? 'No articles from Alpaca. Try refreshing or check your API keys.'
        : 'No articles loaded. RSS proxies may be rate-limited — try again in 2 minutes.';
      return;
    }

    statusEl.textContent = `${this.articles.length} articles · ${new Date().toLocaleTimeString()}`;
    this.renderArticles(this.articles);
  }

  private fromAlpaca(a: AlpacaArticle): Article {
    return { ticker:a.ticker, source:a.source, title:a.title, link:a.link, date:a.date, description:a.description, flag:a.flag };
  }

  private async fetchRSS(url: string, source: string, ticker: string): Promise<Article[]> {
    const cached = RSS_CACHE.get(url);
    if (cached && Date.now() - cached.ts < RSS_TTL) return cached.data;

    const workerBase = getWorkerBase();
    const proxies = [
      `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(url)}&count=8`,
      ...(workerBase ? [`${workerBase}/api/proxy?url=${encodeURIComponent(url)}`] : []),
    ];

    for (const proxy of proxies) {
      try {
        const res = await fetch(proxy, { signal: AbortSignal.timeout(6000) });
        if (!res.ok) continue;
        const raw = await res.json() as any;
        let items: Article[] = [];

        if (raw.items) {
          items = raw.items.map((item: any) => ({
            ticker, source,
            title: item.title || 'No title',
            link:  item.link  || '#',
            date:  item.pubDate || item.published || '',
            description: item.description ? String(item.description).replace(/<[^>]+>/g,'').substring(0,160)+'…' : '',
            flag: flagArticle(item.title || ''),
          }));
        } else if (raw.contents) {
          const xml = new DOMParser().parseFromString(raw.contents, 'text/xml');
          items = Array.from(xml.querySelectorAll('item')).slice(0, 8).map(item => {
            const title = item.querySelector('title')?.textContent || 'No title';
            return {
              ticker, source,
              title,
              link:  item.querySelector('link')?.textContent || item.querySelector('guid')?.textContent || '#',
              date:  item.querySelector('pubDate')?.textContent || '',
              description: (item.querySelector('description')?.textContent || '').replace(/<[^>]+>/g,'').substring(0,160) + '…',
              flag:  flagArticle(title),
            };
          });
        }

        if (items.length) { RSS_CACHE.set(url, { data: items, ts: Date.now() }); return items; }
      } catch { continue; }
    }
    return [];
  }

  // ── Render + filter ───────────────────────────────────────────────────────

  private renderArticles(articles: Article[]): void {
    const feed = this.el('news-feed');
    feed.innerHTML = '';
    if (!articles.length) { this.el('news-empty').style.display = 'block'; return; }
    this.el('news-empty').style.display = 'none';

    for (const a of articles) {
      const div = document.createElement('div');
      div.className     = 'news-item';
      div.dataset.ticker = a.ticker;
      div.dataset.source = a.source;
      div.dataset.flag   = a.flag;
      div.dataset.title  = a.title.toLowerCase();
      div.innerHTML = `
        <div style="display:flex;gap:10px;align-items:flex-start;padding:12px 14px;background:var(--panel);border:1px solid var(--border);border-radius:3px;transition:background 0.15s;" onmouseover="this.style.background='rgba(0,212,255,0.04)'" onmouseout="this.style.background='var(--panel)'">
          <div style="font-size:14px;min-width:20px;text-align:center;margin-top:1px;">${a.flag}</div>
          <div style="flex:1;min-width:0;">
            <a href="${esc(a.link)}" target="_blank" rel="noopener" style="color:var(--text);font-size:12px;font-weight:600;line-height:1.4;text-decoration:none;display:block;" onmouseover="this.style.color='var(--accent)'" onmouseout="this.style.color='var(--text)'">${esc(a.title)}</a>
            ${a.description ? `<div style="color:var(--text3);font-size:11px;margin-top:4px;line-height:1.5;">${esc(a.description)}</div>` : ''}
          </div>
          <div style="display:flex;flex-direction:column;align-items:flex-end;gap:4px;min-width:120px;">
            <span style="display:inline-block;background:rgba(0,212,255,0.1);color:var(--accent);border:1px solid rgba(0,212,255,0.2);padding:1px 7px;border-radius:2px;font-size:10px;font-weight:700;letter-spacing:0.05em;">${esc(a.ticker)}</span>
            <span style="font-size:10px;color:${sourceColor(a.source)};letter-spacing:0.05em;">${esc(a.source)}</span>
            <span style="font-size:10px;color:var(--text3);">${timeAgo(a.date)}</span>
          </div>
        </div>`;
      feed.appendChild(div);
    }
  }

  private filter(): void {
    const search = (this.el('news-search') as HTMLInputElement).value.toLowerCase();
    const ticker = (this.el('news-ticker-filter') as HTMLSelectElement).value;
    const source = (this.el('news-source-filter') as HTMLSelectElement).value;
    const flag   = (this.el('news-flag-filter')   as HTMLSelectElement).value;

    const filtered = this.articles.filter(a => {
      if (ticker && a.ticker !== ticker) return false;
      if (source && a.source !== source) return false;
      if (flag   && a.flag   !== flag)   return false;
      if (search && !a.title.includes(search) && !a.description.toLowerCase().includes(search)) return false;
      return true;
    });

    this.el('news-article-count').textContent = String(filtered.length);
    this.renderArticles(filtered);
  }
}
